/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import {
	TGpsrProductTypesRequest,
	TGpsrProductTypesResponsePayload,
	TGpsrSkuRequest,
	TGpsrSkuResponsePayload,
	TGpsrStatusResponsePayload,
	TGpsrAttachSkuRequest,
	TGpsrDeleteSkuRequest,
	TGpsrGenerateDocRequest,
	TGpsrUploadDocRequest,
	TGpsrDownloadDocRequest,
	TGpsrGenerateDocResponsePayload,
	TGpsrDownloadDocResponsePayload,
	TGpsrUpdateDescriptionRequest,
	TGpsrAttachSkuResponsePayload,
	TGpsrUploadTechFileRequest,
	TGpsrGetTechFileRequest,
	TGpsrGetTechFileResponsePayload,
	TGpsrAdditionalInformationResponsePayload,
	TGpsrCancellationRequest,
	TGpsrPlansResponsePayload,
	TChangeGpsrServiceRequest,
} from './types';
import {
	getGpsrStatusRequest,
	getGpsrStatusSuccess,
	getGpsrProductTypesRequest,
	getGpsrProductTypesSuccess,
	getGpsrSkusRequest,
	getGpsrSkusSuccess,
	attachSkusRequest,
	attachSkusSuccess,
	deleteSkuRequest,
	deleteSkuSuccess,
	generateGpsrDocRequest,
	generateGpsrDocSuccess,
	uploadGpsrDocRequest,
	uploadGpsrDocSuccess,
	downloadGpsrDocRequest,
	downloadGpsrDocSuccess,
	updateGpsrDescription,
	updateGpsrDescriptionSuccess,
	uploadGpsrTechFileRequest,
	uploadGpsrTechFileSuccess,
	getGpsrTechFileRequest,
	getGpsrTechFileSuccess,
	getGpsrAdditionalInformationSuccess,
	getGpsrCancellationSuccess,
	getGpsrAdditionalInformationRequest,
	getGpsrCancellationRequest,
	getGpsrPlansRequest,
	getGpsrPlansSuccess,
	changeGpsrServiceRequest,
	changeGpsrServiceSuccess,
	confirmGpsrAdditionalInformationRequest,
	confirmGpsrAdditionalInformationSuccess,
} from './reducer';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

function* getGpsrStatusRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrStatusResponsePayload = yield call(api.gpsr.getGpsrStatus);
		yield put(getGpsrStatusSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* getGpsrProductTypesRequestWorker(action: PayloadAction<TGpsrProductTypesRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrProductTypesResponsePayload = yield call(
			api.gpsr.getGpsrProductTypes,
			action.payload,
		);
		yield put(getGpsrProductTypesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* getGpsrSkusRequestWorker(action: PayloadAction<TGpsrSkuRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrSkuResponsePayload = yield call(api.gpsr.getGpsrSkus, action.payload);
		yield put(getGpsrSkusSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* attachSkusRequestWorker(action: PayloadAction<TGpsrAttachSkuRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrAttachSkuResponsePayload = yield call(api.gpsr.attachSkus, action.payload);
		yield put(attachSkusSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* deleteSkuRequestWorker(action: PayloadAction<TGpsrDeleteSkuRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.deleteSku, action.payload);
		yield put(deleteSkuSuccess());
		notificationContainer('SKU deleted successfully', 'success');
		setTimeout(() => {
			document.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* generateGpsrDocRequestWorker(
	action: PayloadAction<TGpsrGenerateDocRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrGenerateDocResponsePayload = yield call(
			api.gpsr.generateGpsrDoc,
			action.payload,
		);
		if (response) {
			yield fileDownload(response, 'generated_gpsr_declaration_of_conformity.pdf');
		}
		yield put(generateGpsrDocSuccess());
		notificationContainer('Document generated successfully', 'success');
		setTimeout(() => {
			document.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(generateGpsrDocSuccess());
	}
}

function* uploadGpsrDocRequestWorker(action: PayloadAction<TGpsrUploadDocRequest>) {
	try {
		const { payload } = action;
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.uploadGpsrDoc, payload);
		yield put(uploadGpsrDocSuccess());
		notificationContainer('Document uploaded successfully', 'success');
		setTimeout(() => {
			document.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File upload error',
				'error',
			);
		yield put(uploadGpsrDocSuccess());
	}
}

function* downloadGpsrDocRequestWorker(
	action: PayloadAction<TGpsrDownloadDocRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrDownloadDocResponsePayload = yield call(
			api.gpsr.downloadGpsrDoc,
			action.payload,
		);
		if (response) {
			yield fileDownload(response, 'signed_gpsr_declaration_of_conformity.pdf');
		}
		yield put(downloadGpsrDocSuccess());
		notificationContainer('Document downloaded successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(downloadGpsrDocSuccess());
	}
}

export function* updateGpsrDescriptionWorker(action: PayloadAction<TGpsrUpdateDescriptionRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.updateGpsrDescription, action.payload);
		yield put(updateGpsrDescriptionSuccess());
		notificationContainer('Description updated successfully', 'success');
		setTimeout(() => {
			document.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(updateGpsrDescriptionSuccess());
	}
}

function* uploadGpsrTechFileRequestWorker(action: PayloadAction<TGpsrUploadTechFileRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.uploadGpsrTechFile, action.payload);
		yield put(uploadGpsrTechFileSuccess());
		notificationContainer('Technical file uploaded successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File upload error',
				'error',
			);
		yield put(uploadGpsrTechFileSuccess());
	}
}

function* getGpsrTechFileRequestWorker(
	action: PayloadAction<TGpsrGetTechFileRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrGetTechFileResponsePayload = yield call(
			api.gpsr.getGpsrTechFile,
			action.payload,
		);
		yield put(getGpsrTechFileSuccess());
		if (response) {
			yield fileDownload(response, 'technical_file.pdf');
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(getGpsrTechFileSuccess());
	}
}

function* getGpsrAdditionalInformationRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrAdditionalInformationResponsePayload = yield call(
			api.gpsr.getGpsrAdditionalInformation,
		);
		yield put(getGpsrAdditionalInformationSuccess(response));
	} catch (error) {
		// 	if (axios.isAxiosError(error))
		// 		notificationContainer(
		// 			error?.response?.data?.message ?? error.message ?? 'File download error',
		// 			'error',
		// 		);
	}
}

function* getGpsrCancellationRequestWorker(action: PayloadAction<TGpsrCancellationRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.getGpsrCancellation);
		yield put(getGpsrCancellationSuccess());
		yield put(getGpsrProductTypesRequest(action.payload.gpsrRequest));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* getGpsrPlansRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGpsrPlansResponsePayload = yield call(api.gpsr.getGpsrPlans);
		yield put(getGpsrPlansSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
	}
}

function* changeGpsrServiceRequestWorker(action: PayloadAction<TChangeGpsrServiceRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.changeGpsrServiceRequest, action.payload);
		yield put(changeGpsrServiceSuccess());
		notificationContainer('Request submitted successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(changeGpsrServiceSuccess());
	}
}

function* confirmGpsrAdditionalInformationRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.gpsr.confirmAdditionalInformation);
		yield put(confirmGpsrAdditionalInformationSuccess(null));
		notificationContainer('Additional information confirmed successfully', 'success');
		yield put(getGpsrAdditionalInformationRequest());
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(confirmGpsrAdditionalInformationSuccess(null));
	}
}

export function* gpsrSaga() {
	yield takeEvery(getGpsrStatusRequest.type, getGpsrStatusRequestWorker);
	yield takeEvery(getGpsrProductTypesRequest.type, getGpsrProductTypesRequestWorker);
	yield takeEvery(getGpsrSkusRequest.type, getGpsrSkusRequestWorker);
	yield takeEvery(attachSkusRequest.type, attachSkusRequestWorker);
	yield takeEvery(deleteSkuRequest.type, deleteSkuRequestWorker);
	yield takeEvery(generateGpsrDocRequest.type, generateGpsrDocRequestWorker);
	yield takeEvery(uploadGpsrDocRequest.type, uploadGpsrDocRequestWorker);
	yield takeEvery(downloadGpsrDocRequest.type, downloadGpsrDocRequestWorker);
	yield takeEvery(updateGpsrDescription.type, updateGpsrDescriptionWorker);
	yield takeEvery(uploadGpsrTechFileRequest.type, uploadGpsrTechFileRequestWorker);
	yield takeEvery(getGpsrTechFileRequest.type, getGpsrTechFileRequestWorker);
	yield takeEvery(
		getGpsrAdditionalInformationRequest.type,
		getGpsrAdditionalInformationRequestWorker,
	);
	yield takeEvery(getGpsrCancellationRequest.type, getGpsrCancellationRequestWorker);
	yield takeEvery(getGpsrPlansRequest.type, getGpsrPlansRequestWorker);
	yield takeEvery(changeGpsrServiceRequest.type, changeGpsrServiceRequestWorker);
	yield takeEvery(
		confirmGpsrAdditionalInformationRequest.type,
		confirmGpsrAdditionalInformationRequestWorker,
	);
}
